'use client';

// npm library imports
import { JSX, useMemo, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';

// style imports
import styles from './slidemenu.module.scss';

// image imports
import crocLogo from '@public/images/logos/logo-white.png';

// component imports
import Icons from '@components/Icons';

// lib imports
import type { navLink, navSection } from '@lib/types';
import { NAVSECTIONS } from '@lib/navdata';

/**
 * Slide Menu component for mobile navigation
 *
 * @param props - The props for the component
 * @param props.onMouseDown - The function to toggle the side menu
 * @param props.menuVisibility - The visibility state of the menu
 * @returns - The Slide Menu component JSX.Element
 *
 * @example
 * ```tsx
 * <SlideMenu
 *  onMouseDown={handleMouseDown}
 *  menuVisibility={menuVisible}
 * />
 * ```
 */
const SlideMenu = ({
    menuVisibility,
    toggle,
}: {
    menuVisibility: boolean;
    toggle: () => void;
}) => {
    /**
     * Creates menu navigation items from NAVSECTIONS data
     *
     * ``` ts
     * [
     *     {
     *         title: "Accommodation",
     *         path: "/accommodation",
     *         navLinks: [
     *             {
     *                 title: "Cabins",
     *                 path: "/accommodation#cabins"
     *             },
     *             {
     *                 title: "Facilities and Services",
     *                 path: "/accommodation#facilities"
     *             },
     *         ]
     *     },
     *     {
     *         title: "Dining",
     *         path: "/dining",
     *         navLinks: [
     *             {
     *                 title: "Warrior Restaurant and Bar",
     *                 path: "/dining#warrior"
     *             }
     *         ]
     *     }
     * ]
     * ```
     */
    const renderMenuItems = useMemo((): JSX.Element => {
        return (
            <ul className={styles.menuitems}>
                {NAVSECTIONS.map((section: navSection, index) => (
                    <li
                        className={styles.menuGroup}
                        key={index}
                        // Close when a link is clicked
                        onClick={(e) => {
                            e.stopPropagation();
                            //e.preventDefault();
                            toggle();
                        }}
                    >
                        <Link href={section.path} className={styles.titleLink}>
                            <h2>{section.title}</h2>
                        </Link>
                        <ul>
                            {section.navLinks.map(
                                (link: navLink, linkIndex) => (
                                    <li key={linkIndex}>
                                        <Link href={link.path}>
                                            {link.title}
                                        </Link>
                                    </li>
                                ),
                            )}
                        </ul>
                    </li>
                ))}
            </ul>
        );
    }, [toggle]);

    // Return memoised CSS className to Show or hide the menu based on menuVisibility state
    const showHideMenuClassName = useMemo(() => {
        return menuVisibility ? styles.show : styles.hide;
    }, [menuVisibility]);

    useEffect(() => {
        if (!menuVisibility) return;
    
        const escPress = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                toggle();
            }
        };

        document.addEventListener('keydown', escPress);

        return () => {
            document.removeEventListener('keydown', escPress);
        };
    }, [menuVisibility, toggle]);



    return (
        <>
            {/* Slide Menu Container */}
            <div className={`${showHideMenuClassName} ${styles.container}`} inert={!menuVisibility} aria-hidden={!menuVisibility} role="navigation" aria-label="Slide menu">
                {/* Sidebar Menu */}
                <div className={`${showHideMenuClassName} ${styles.themenu}`}>
                    {/* Close Button */}
                    <button
                        aria-label="Close Menu"
                        className={styles.close}
                        onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            toggle();
                        }}
                    >
                        {/* Close Icon */}
                        <Icons iconClass={`nav-x`} extraClasses={['hover']} />
                    </button>
                    {/* Logo */}
                    <div className={styles.logo}>
                        <Link href="/">
                            <Image
                                priority
                                src={crocLogo}
                                width={150}
                                height={35}
                                alt="Crocodile Hunter Lodge Logo"
                                placeholder="blur"
                            />
                        </Link>
                    </div>
                    {/* Menu Items */}
                    {renderMenuItems}
                </div>
                {/* Click-off transparent div */}
                <div
                    className={`${styles.clickoff}`}
                    onClick={(e) => {
                        e.preventDefault();
                        toggle();
                    }}
                />
            </div>
        </>
    );
};

export default SlideMenu;
